import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Modal, FileUploadZone } from '../common';
import ImageCropper from './ImageCropper';
import './ProfileIdentity.css';

/**
 * Profile identity section with avatar, username, and stats.
 *
 * Overlaps the banner by 40px to create visual depth.
 * Avatar is uploadable with circular crop preview.
 *
 * @param {Object} props
 * @param {string|null} props.avatarUrl - Current avatar image URL
 * @param {function} props.onAvatarChange - Handler for avatar updates
 * @param {string} props.username - User's display name
 * @param {string} [props.joinedDate='today'] - When user joined
 * @param {number} [props.followers=0] - Follower count
 * @returns {JSX.Element}
 */
function ProfileIdentity({
  avatarUrl,
  onAvatarFile,
  username,
  isPro = false,
  joinedDate = null,
  followers = 0,
  following = 0,
  uploading = false,
  onFollowersClick = null,
  onFollowingClick = null,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState(null);
  const [tempImageFile, setTempImageFile] = useState(null);
  const [showCropper, setShowCropper] = useState(false);

  const handleFileSelect = useCallback((file) => {
    if (file && file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setTempImageFile(file);
      setTempImageUrl(url);
      setShowCropper(true);
    }
  }, []);

  const handleCropComplete = useCallback(
    async (croppedFile) => {
      const fileToUpload = croppedFile || tempImageFile;
      if (!fileToUpload) return;
      try {
        await onAvatarFile(fileToUpload);
      } catch (error) {
        console.error('Failed to upload avatar image:', error);
      } finally {
        if (tempImageUrl) URL.revokeObjectURL(tempImageUrl);
        setTempImageFile(null);
        setTempImageUrl(null);
        setShowCropper(false);
        setIsModalOpen(false);
      }
    },
    [onAvatarFile, tempImageFile, tempImageUrl]
  );

  const handleCropCancel = useCallback(() => {
    if (tempImageUrl) URL.revokeObjectURL(tempImageUrl);
    setTempImageFile(null);
    setTempImageUrl(null);
    setShowCropper(false);
  }, [tempImageUrl]);

  const handleModalClose = () => {
    if (tempImageUrl) URL.revokeObjectURL(tempImageUrl);
    setIsModalOpen(false);
    setTempImageFile(null);
    setTempImageUrl(null);
    setShowCropper(false);
  };

  const joinedLabel = joinedDate ? `Joined ${joinedDate}` : null;

  return (
    <>
      <div className="profile-identity">
        {/* Avatar */}
        <div className="profile-identity__avatar-section">
          <button
            type="button"
            className="profile-identity__avatar"
            onClick={() => setIsModalOpen(true)}
            aria-label="Change profile picture"
            disabled={uploading}
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={username}
                className="profile-identity__avatar-image"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="profile-identity__avatar-placeholder">
                <span className="profile-identity__avatar-icon">👤</span>
              </div>
            )}
            <div className="profile-identity__avatar-overlay">
              <span>{uploading ? '⏳' : '📷'}</span>
              <span>{uploading ? 'Saving…' : 'Upload'}</span>
            </div>
          </button>
          <button
            type="button"
            className="profile-identity__add-profile"
            onClick={() => setIsModalOpen(true)}
            disabled={uploading}
          >
            {uploading ? 'Saving…' : 'Add profile'}
          </button>
        </div>

        {/* Info */}
        <div className="profile-identity__info">
          <div className="profile-identity__name-row">
            <h2 className="profile-identity__username">{username}</h2>
            {isPro && (
              <span className="profile-identity__pro-badge" title="Discovery Plan PRO">
                PRO
              </span>
            )}
            <span className="profile-identity__badge" title="Verified">✓</span>
            <span className="profile-identity__link-icon" title="Connected platform">🔗</span>
          </div>
          {joinedLabel && (
            <p className="profile-identity__joined">{joinedLabel}</p>
          )}
          <div className="profile-identity__social-stats">
            {onFollowersClick ? (
              <button
                type="button"
                className="profile-identity__followers profile-identity__followers-btn"
                onClick={onFollowersClick}
              >
                {followers} followers
              </button>
            ) : (
              <p className="profile-identity__followers">{followers} followers</p>
            )}
            <span className="profile-identity__followers-separator">•</span>
            {onFollowingClick ? (
              <button
                type="button"
                className="profile-identity__followers profile-identity__followers-btn"
                onClick={onFollowingClick}
              >
                {following} following
              </button>
            ) : (
              <p className="profile-identity__followers">{following} following</p>
            )}
          </div>
        </div>

      </div>

      {/* Avatar Upload Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title="Add Profile Picture"
      >
        {showCropper && tempImageUrl ? (
          <ImageCropper
            imageUrl={tempImageUrl}
            sourceFile={tempImageFile}
            onComplete={handleCropComplete}
            onCancel={handleCropCancel}
          />
        ) : (
          <FileUploadZone
            onFileSelect={handleFileSelect}
            accept="image/*"
            prompt="Drop your photo here or click to upload"
            hint="Recommended: Square image, at least 200x200 pixels"
            icon={<span>📷</span>}
          />
        )}
      </Modal>
    </>
  );
}

ProfileIdentity.propTypes = {
  avatarUrl: PropTypes.string,
  onAvatarFile: PropTypes.func.isRequired,
  username: PropTypes.string.isRequired,
  isPro: PropTypes.bool,
  joinedDate: PropTypes.string,
  followers: PropTypes.number,
  following: PropTypes.number,
  uploading: PropTypes.bool,
  onFollowersClick: PropTypes.func,
  onFollowingClick: PropTypes.func,
};

export default ProfileIdentity;
